import React, { useCallback, type SyntheticEvent, useMemo } from 'react';
import { xcss, Pressable, Box } from '@atlaskit/primitives';
import AddIcon from '@atlaskit/icon/core/add';
import { token } from '@atlaskit/tokens';
import { asyncInlineCreateStatusModal } from '@atlassian/jira-inline-create-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { STATUS_TYPE } from '@atlassian/jira-issue-agg-field-transformers/src/common/constants.tsx';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { createStatusExperience } from '../../../../experiences.tsx';
import { useListViewRefreshCacheActions } from '../../../../controllers/list-view-refresh-cache/ListViewRefreshCacheStore.tsx';
import { useGroupBy } from '../../../../controllers/group-by/index.tsx';
import messages from './messages.tsx';
import type { CreateStatusButtonProps } from './types.tsx';

export const CreateStatusButton = ({
	statusNames,
	onCreateStatusSuccess,
	projectId,
	projectKey,
	projectType,
	issueTypeId,
	isNoResultsView,
	shouldReturnFocus,
	buttonRef = null,
	isButtonWithIcon,
	hasMultipleWorkflows,
	analyticsSource,
}: CreateStatusButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const [groupByFieldId] = useGroupBy();
	const isGroupingByStatus = groupByFieldId === STATUS_TYPE;
	const { triggerListViewGroupByCacheRefresh, triggerWorkflowCacheRefresh } = fg(
		'refetch_status_on_list_group_by_status',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useListViewRefreshCacheActions()
		: { triggerListViewGroupByCacheRefresh: undefined, triggerWorkflowCacheRefresh: undefined };

	const onStatusCreated = useCallback(() => {
		if (isGroupingByStatus) {
			triggerWorkflowCacheRefresh?.();
			triggerListViewGroupByCacheRefresh?.();
		}
		onCreateStatusSuccess?.();
	}, [
		isGroupingByStatus,
		onCreateStatusSuccess,
		triggerListViewGroupByCacheRefresh,
		triggerWorkflowCacheRefresh,
	]);

	const isPressable = fg('inline_config_a11y_improvements')
		? isNoResultsView || isButtonWithIcon
		: true;

	const handleClick = useCallback(
		(e: SyntheticEvent) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'createStatusButton',
				{ source: analyticsSource },
			);

			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleTransitionChange
			// will be called which will trigger click manually again and cause double modal rendering
			e.stopPropagation();
		},
		[analyticsSource, createAnalyticsEvent],
	);

	const onModalClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineCreateStatusModal',
			}),
			{ source: analyticsSource },
		);
	}, [analyticsSource, createAnalyticsEvent]);

	const entryPointProps = useMemo(
		() => ({
			statusNames,
			projectId,
			projectKey,
			projectType,
			onClose: onModalClose,
			isNewControllerEnabled: true,
			workflowAssociatedIssueTypeId: issueTypeId,
			onStatusCreated,
			createStatusExperience,
			source: analyticsSource,
			hasMultipleWorkflows,
		}),
		[
			statusNames,
			projectId,
			projectKey,
			projectType,
			onModalClose,
			issueTypeId,
			onStatusCreated,
			analyticsSource,
			hasMultipleWorkflows,
		],
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			projectKey,
		}),
		[cloudId, projectKey],
	);

	const Wrapper = isPressable ? Pressable : Box;

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineCreateStatusModal}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="create-status-modal"
			modalProps={{ ...defaultModalProps, shouldReturnFocus }}
			forwardedRef={buttonRef}
			useInternalModal
			Fallback={
				fg('jgfeng_new_work_type_fallback_loading_state')
					? () => <FormDialogSkeleton numFormFields={2} />
					: undefined
			}
		>
			{({ ref }) => (
				<Wrapper
					onClick={handleClick}
					backgroundColor="color.background.neutral.subtle"
					xcss={[
						isNoResultsView ? createStatusInlineNoResultStyles : createStatusInlineStyles,
						isButtonWithIcon && createStatusInlineWithIconStyles,
					]}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={ref as React.Ref<HTMLButtonElement>}
				>
					{isButtonWithIcon && (
						<Box xcss={iconButtonStyles}>
							<AddIcon label="" />
						</Box>
					)}
					{formatMessage(messages.createStatus)}
				</Wrapper>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const createStatusInlineNoResultStyles = xcss({
	marginLeft: 'space.150',
	color: 'color.link',
	fontWeight: 'font.weight.bold',
});

const createStatusInlineStyles = xcss({
	display: 'flex',
	width: '100%',
	padding: 'space.150',
	color: 'color.text',
	fontWeight: 'font.weight.medium',
});

const createStatusInlineWithIconStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
	':focus-visible': {
		// @ts-expect-error - outlineOffset currently does not support negative tokens - see https://atlassian.slack.com/archives/CFJ9DU39U/p1733863467395999
		outlineOffset: token('space.negative.025'),
		outlineStyle: 'auto',
		outlineWidth: 'border.width.outline',
		outlineColor: 'color.border.focused',
	},
});

const iconButtonStyles = xcss({
	paddingRight: 'space.100',
	paddingLeft: 'space.100',
});
